import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";

interface LegalLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

export default function LegalLayout({ title, lastUpdated, children }: LegalLayoutProps) {
  // Legal pages are deep links; always start at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-ink min-h-screen">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 pt-32 pb-24 lg:pt-40 lg:pb-32">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono text-xs text-chalk-3 tracking-widest uppercase hover:text-acid-soft transition-colors duration-200"
        >
          <i className="ri-arrow-left-line" />
          Back to home
        </Link>

        <h1 className="mt-8 font-display font-bold text-[clamp(2.25rem,5vw,3.75rem)] text-chalk tracking-tightest leading-none">
          {title}
        </h1>
        <p className="mt-4 font-mono text-xs text-chalk-3 tracking-widest uppercase">
          Last updated: {lastUpdated}
        </p>

        <div className="legal-prose mt-12 space-y-8">{children}</div>
      </main>
    </div>
  );
}

// Small typographic helpers so each legal page reads cleanly without repeating classes.
export function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display font-bold text-xl lg:text-2xl text-chalk tracking-tight leading-snug">
        {heading}
      </h2>
      <div className="mt-4 space-y-4 text-chalk-2 text-[15px] leading-relaxed">{children}</div>
    </section>
  );
}

export function P({ children }: { children: React.ReactNode }) {
  return <p>{children}</p>;
}

export function List({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 w-1 h-1 rounded-full bg-acid shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}
